// Pure health helpers shared by the Unit Health screen and the Dashboard
// heads-up sections. No Firebase — takes already-loaded docs.

import type { Unit, UnitAdvancementLatest, UnitType, YptStatus } from '../types';

/** Display order + plural labels for the unit-type filter chips. */
export const UNIT_TYPE_ORDER: UnitType[] = [
  'pack',
  'troop',
  'crew',
  'ship',
  'post',
  'club',
  'other',
];
export const UNIT_TYPE_PLURAL: Record<UnitType, string> = {
  pack: 'Packs',
  troop: 'Troops',
  crew: 'Crews',
  ship: 'Ships',
  post: 'Posts',
  club: 'Clubs',
  other: 'Other',
};

export type Tone = 'ok' | 'amber' | 'red';

export function daysUntil(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(y, m - 1, d).getTime();
  return Math.round((target - Date.now()) / 86_400_000);
}

/** Recharter countdown + tone: red inside 30 days (or past due), amber inside
 *  60, otherwise ok. Null when the unit has no recharter record. */
export function rechartCountdown(u: Unit): { days: number; tone: Tone } | null {
  if (!u.recharter) return null;
  const days = daysUntil(u.recharter.charterExpires);
  const tone: Tone = days < 30 ? 'red' : days < 60 ? 'amber' : 'ok';
  return { days, tone };
}

/** Which of the three charter states a unit is in — for the stacked bar and
 *  the alert strip. A unit with no recharter record is 'unknown'. */
export function rechartBucket(u: Unit): 'chartered' | 'soon' | 'overdue' | 'unknown' {
  if (!u.recharter) return 'unknown';
  const days = daysUntil(u.recharter.charterExpires);
  if (days < 0) return 'overdue';
  if (days <= 60) return 'soon';
  return 'chartered';
}

export const YPT_TONE: Record<YptStatus, Tone | null> = {
  active: 'ok',
  expiring: 'amber',
  expired: 'red',
  unknown: null,
};

/** Missing Key 3 roles + tone. Red if the Committee Chair or unit leader seat
 *  is empty, amber for a missing Charter Org Rep only, ok when all three are
 *  filled. Null when the unit has no Key 3 record. */
export function leadershipTone(u: Unit): { tone: Tone; missing: string[] } | null {
  if (!u.key3) return null;
  const missing: string[] = [];
  if (!u.key3.hasCommitteeChair) missing.push('Committee Chair');
  if (!u.key3.hasUnitLeader) missing.push('Unit leader');
  if (!u.key3.hasCharterOrgRep) missing.push('Charter Org Rep');
  const tone: Tone =
    !u.key3.hasCommitteeChair || !u.key3.hasUnitLeader ? 'red' : missing.length ? 'amber' : 'ok';
  return { tone, missing };
}

export interface AdvState {
  tone: Tone;
  label: 'Stalled' | 'Behind' | 'On track';
  snap: UnitAdvancementLatest;
}

/** Advancement pace for a unit against its latest advancement. Stalled = youth
 *  registered but zero ranks earned YTD; packs also flag 'Behind' under 50%.
 *  Null when there's no advancement data. */
export function advancementState(
  u: Unit,
  snap: UnitAdvancementLatest | undefined,
): AdvState | null {
  if (!snap) return null;
  const youth = snap.youth || 0;
  if (snap.ranksYtd === 0 && youth > 0) return { tone: 'red', label: 'Stalled', snap };
  if (u.type === 'pack' && youth > 0 && snap.ranksYtd < youth * 0.5) {
    return { tone: 'amber', label: 'Behind', snap };
  }
  return { tone: 'ok', label: 'On track', snap };
}

/** Unit types with a rank ladder — Posts/Clubs/Crews/Ships are excluded from
 *  advancement pace stats (they show "—" on the Unit Health screen). */
export function hasRankLadder(u: Unit): boolean {
  return u.type === 'pack' || u.type === 'troop';
}

/** The program a unit's rank pace is judged against. */
export function advProgram(u: Unit): 'cub' | 'scoutsBSA' {
  return u.type === 'pack' ? 'cub' : 'scoutsBSA';
}

/** The unit's current advancement for its primary program — read straight off
 *  the denormalized `latestAdvancement` field (see src/lib/units.ts). */
export function advForUnit(u: Unit): UnitAdvancementLatest | undefined {
  return u.latestAdvancement?.[advProgram(u)] ?? undefined;
}

/** A specific program's current advancement for a unit. */
export function advForUnitProgram(
  u: Unit,
  program: 'cub' | 'scoutsBSA',
): UnitAdvancementLatest | undefined {
  return u.latestAdvancement?.[program] ?? undefined;
}

/** Tone from a value measured against ok/warn cutoffs (value at or above `ok`
 *  is green, at or above `warn` is amber, below is red). */
export function toneFor(value: number, ok: number, warn: number): Tone {
  if (value >= ok) return 'ok';
  if (value >= warn) return 'amber';
  return 'red';
}

// ---- Unit Health filter presets --------------------------------------
// Named views the dashboard links into ("click the alert, land on those
// units"). The Units view and the Leaders view have their own set.

export type UnitFilter =
  | 'all'
  | 'recharterOverdue'
  | 'recharterDue60'
  | 'leadershipGap'
  | 'sytFlag'
  | 'stalled'
  | 'noCommissioner';

export type LeaderFilter =
  | 'all'
  | 'keyThree'
  | 'untrained'
  | 'sytNotCurrent'
  | 'hazardousWeather'
  | 'councilExecBoard';

/** Is a Trained-Leaders position one of the Key 3 roles — the unit's
 *  Committee Chair, its top program leader, or its Chartered Organization
 *  Representative, plus the district Key 3. Excludes assistants and
 *  committee members. Matched on the report's raw position text. */
export function isKeyThreeRole(position: string): boolean {
  const p = position.trim().toLowerCase();
  if (p.startsWith('assistant') || p.startsWith('associate')) return false;
  if (/committee chair(man|person)?$/.test(p)) return true; // "Pack Committee Chair", "Committee Chairman"
  if (/^(cubmaster|scoutmaster|skipper|coach)$/.test(p)) return true;
  if (/^(crew )?advisor$/.test(p)) return true;
  if (/chartered organization representative$/.test(p)) return true;
  if (/^charter(ed)? org(anization)? rep(resentative)?$/.test(p)) return true;
  if (/^district (chair(man|person)?|commissioner|executive|director)$/.test(p)) return true;
  return false;
}

/** Is a Trained-Leaders position Council Executive Board Member. Verified
 *  against real production data (Blackhawk tenant, 2,443 unitLeaders docs,
 *  35 distinct position strings on file): "Council Executive Board Member"
 *  is the only string this role appears as — always council scope, no
 *  abbreviations or casing variants on file. Distinct council-scope roles
 *  that also mention "council"/"board" (Council Commissioner, Council
 *  President, Council Vice-President, Assistant Council Commissioner) are
 *  genuinely different positions, not variants of this one, so they're
 *  intentionally excluded. Matched case-insensitively/trimmed as
 *  defense-in-depth against incidental formatting drift in a future
 *  import, not because a variant has actually been observed. */
export function isCouncilExecutiveBoardRole(position: string): boolean {
  return position.trim().toLowerCase() === 'council executive board member';
}

export const UNIT_FILTER_LABELS: Record<Exclude<UnitFilter, 'all'>, string> = {
  recharterOverdue: 'Recharter overdue',
  recharterDue60: 'Recharter due ≤ 60 days',
  leadershipGap: 'Leadership gap',
  sytFlag: 'Key 3 SYT flag',
  stalled: 'Stalled advancement',
  noCommissioner: 'No commissioner assigned',
};

export const LEADER_FILTER_LABELS: Record<Exclude<LeaderFilter, 'all'>, string> = {
  keyThree: 'Key 3 only',
  untrained: 'Not fully trained',
  sytNotCurrent: 'SYT not current',
  hazardousWeather: 'Hazardous Weather',
  councilExecBoard: 'Council Executive Board',
};

export function unitMatchesFilter(
  u: Unit,
  filter: UnitFilter,
  adv: UnitAdvancementLatest | undefined,
  /** Ids of units that have at least one active commissioner link (the 'noCommissioner' preset needs it). */
  unitsWithCommissioner?: ReadonlySet<string>,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'recharterOverdue': {
      const c = rechartCountdown(u);
      return !!c && c.days < 0;
    }
    case 'recharterDue60': {
      const c = rechartCountdown(u);
      return !!c && c.days >= 0 && c.days <= 60;
    }
    case 'leadershipGap': {
      const l = leadershipTone(u);
      return !!l && l.missing.length > 0;
    }
    case 'sytFlag':
      return !!u.key3 && (u.key3.worstYpt === 'expired' || u.key3.worstYpt === 'expiring');
    case 'stalled':
      return advancementState(u, adv)?.label === 'Stalled';
    case 'noCommissioner':
      return !!unitsWithCommissioner && !unitsWithCommissioner.has(u.id);
  }
}

// ---- Hazardous Weather Training (course SCO_800) ---------------------
//
// Canonical evaluator — the ONE place Compass decides whether Hazardous
// Weather Training applies to a person and, if so, what their status is.
// The Leaders table, the "Hazardous Weather" filter, and the leader
// detail drawer all call THIS function rather than re-deriving the
// answer from raw incompleteCourses/directContact fields themselves.
//
// functions/src/hazardousWeatherAutomation.ts carries an independent but
// logically identical copy of this evaluator — Cloud Functions can't
// import from src/ (same constraint already accepted for SYT/Advancement/
// Renewal's own tier logic; see those files' header comments) — so
// "centralized" means one canonical evaluator PER SIDE, kept in sync by
// convention and by the test suites on both sides, not one literal shared
// module across the client/Functions boundary.
//
// ---- WHAT NATIONAL'S TRAINED LEADERS STATUS REPORT ACTUALLY PROVIDES ----
// Inspected before writing this (src/lib/unitReports.ts's
// parseTrainedLeadersReport + real production data): each row carries a
// `directContact` boolean (BSA's OWN "direct contact leader" flag — see
// below) and a list of currently-INCOMPLETE course codes for that
// position. There is NO per-course completion date and NO per-course
// expiration date anywhere in this report — only "is SCO_800 currently
// owed, yes or no." Compass also does not retain history across imports:
// `unitLeaders` is wiped and rewritten whole on every import (see
// lib/units.ts), so there's no prior-snapshot trail to infer "this used
// to be satisfied" from either. CONCLUSION: from today's real data alone,
// Compass can tell "currently satisfied" from "currently owed," but
// CANNOT tell "never completed" from "completed once, now expired" —
// so `expired` is only ever produced when a `completedDate` is
// explicitly supplied (see below), which nothing in the current import
// pipeline does. Everyone who currently owes SCO_800 reads as `missing`,
// not `expired` — the honest, less-specific classification, per the
// explicit instruction to prefer that over inventing an expiration.
//
// ---- APPLICABILITY: BSA'S OWN directContact FLAG, NOT A NAME LIST ----
// The spec asked for a position-code/name allowlist (Cubmaster, Den
// Leader, Scoutmaster, Advisor, Skipper, …). Real production data shows
// something better already sitting in the import: BSA's own
// `direct_contact_leader` flag (parsed into `directContact` on every
// position row) is true for EXACTLY the positions Hazardous Weather
// applies to and false for every committee/council/district role —
// verified against all 35 distinct position strings actually on file
// (Assistant Scoutmaster/Den Leader/Scoutmaster/Cubmaster/Asst. Den
// Leader/Explorer Post Advisor/etc. all directContact=true; Committee
// Member/Committee Chair/Chartered Org Rep/Merit Badge Counselor/Council
// Executive Board Member/Unit Commissioner/every district-or-council
// role all directContact=false — a 100% match against the spec's own
// applicable/non-applicable lists). This is MORE robust than a
// hand-maintained name list (survives spelling variants like "Den
// Leader" vs "Asst. Den Leader", a position this council has never
// imported, a future rename) and is itself National/canonical data, not
// Compass-invented — exactly what "prefer stable codes over display-name
// string matching" was asking for, just not in the literal shape
// expected. isHazardousWeatherApplicableRole() is the one-line
// centralized predicate; the name list from the spec is preserved only
// in this comment as the human-readable cross-reference it was verified
// against.

export const HAZARDOUS_WEATHER_COURSE_CODE = 'SCO_800';
/** Scouting America: Hazardous Weather Training is valid for 2 years. */
export const HAZARDOUS_WEATHER_VALIDITY_YEARS = 2;
/** Default "approaching expiration" warning window — configurable (one
 *  named constant), never a bare 60 scattered through the logic. */
export const HAZARDOUS_WEATHER_DUE_SOON_DAYS = 60;

export type HazardousWeatherStatus = 'current' | 'due_soon' | 'expired' | 'missing' | 'not_applicable';

/** Tone for the compact dot — 'not_applicable' has none (rendered as a
 *  muted em dash instead, never a colored dot implying compliance with a
 *  requirement that doesn't apply to this person). */
export const HAZARDOUS_WEATHER_TONE: Record<HazardousWeatherStatus, Tone | null> = {
  current: 'ok',
  due_soon: 'amber',
  expired: 'red',
  missing: 'red',
  not_applicable: null,
};

export const HAZARDOUS_WEATHER_STATUS_LABELS: Record<HazardousWeatherStatus, string> = {
  current: 'Current',
  due_soon: 'Due soon',
  expired: 'Expired',
  missing: 'Missing',
  not_applicable: '—',
};

/** The minimal shape the evaluator needs from one of a person's
 *  registered positions — matches LeaderRole/LeaderPositionEntry's own
 *  fields, kept structural (not a direct import of either) so this file
 *  has no dependency on the view layer. */
export interface HazardousWeatherRoleInput {
  position: string; // display name, e.g. "Scoutmaster" — for the reason text only
  where: string; // e.g. "Troop 0084 (B)" — for the reason text only
  directContact: boolean;
  incompleteCourses: string[];
}

export interface HazardousWeatherApplicablePosition {
  position: string;
  where: string;
}

export interface HazardousWeatherEvaluation {
  applicable: boolean;
  status: HazardousWeatherStatus;
  /** Every current position that requires Hazardous Weather — empty when
   *  not applicable. */
  applicablePositions: HazardousWeatherApplicablePosition[];
  /** Only ever non-null when a completedDate was supplied to the
   *  evaluator — nothing in today's real import pipeline supplies one
   *  (see the file-level note above). */
  completedDate: string | null;
  /** Derived from completedDate + HAZARDOUS_WEATHER_VALIDITY_YEARS when
   *  completedDate is known; otherwise null (an unknown-but-currently-
   *  satisfied person has no computable expiration or countdown). */
  expirationDate: string | null;
  daysRemaining: number | null;
  /** A short, human-readable explanation of the result — used as the
   *  filter/drawer's "why" text and as an Action's description seed. */
  reason: string;
}

export function isHazardousWeatherApplicableRole(role: Pick<HazardousWeatherRoleInput, 'directContact'>): boolean {
  return role.directContact === true;
}

function addYearsIso(iso: string, years: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y + years, m - 1, d));
  return dt.toISOString().slice(0, 10);
}

function daysBetweenIso(fromIso: string, toIso: string): number {
  const [fy, fm, fd] = fromIso.split('-').map(Number);
  const [ty, tm, td] = toIso.split('-').map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

function todayIsoLocal(): string {
  return new Date().toISOString().slice(0, 10);
}

function positionsText(positions: HazardousWeatherApplicablePosition[]): string {
  return positions.map((p) => p.position).join(', ');
}

/** The canonical Hazardous Weather evaluator. `roles` should be ALL of a
 *  person's CURRENT registered positions across every unit/district/
 *  council scope — applicability is evaluated across the whole set (a
 *  Committee Member in one unit who is also a Scoutmaster elsewhere IS
 *  applicable), never a single position in isolation.
 *
 *  `opts.completedDate`, when supplied, is the one piece of authoritative
 *  data that unlocks `due_soon`/`expired`/a real daysRemaining countdown
 *  — see the file header for why nothing in Compass's current import
 *  pipeline ever actually supplies it today. Without it, an applicable
 *  person reads as `current` (National doesn't list SCO_800 as owed) or
 *  `missing` (National does) — never `expired`, never a day count. */
export function evaluateHazardousWeather(
  roles: HazardousWeatherRoleInput[],
  opts: { completedDate?: string | null; today?: string; dueSoonDays?: number } = {},
): HazardousWeatherEvaluation {
  const today = opts.today ?? todayIsoLocal();
  const dueSoonDays = opts.dueSoonDays ?? HAZARDOUS_WEATHER_DUE_SOON_DAYS;

  const applicableRoles = roles.filter(isHazardousWeatherApplicableRole);
  if (applicableRoles.length === 0) {
    return {
      applicable: false,
      status: 'not_applicable',
      applicablePositions: [],
      completedDate: null,
      expirationDate: null,
      daysRemaining: null,
      reason: 'Not required for this person’s current position(s).',
    };
  }

  const applicablePositions = applicableRoles.map((r) => ({ position: r.position, where: r.where }));
  const who = positionsText(applicablePositions);

  if (opts.completedDate) {
    const expirationDate = addYearsIso(opts.completedDate, HAZARDOUS_WEATHER_VALIDITY_YEARS);
    const daysRemaining = daysBetweenIso(today, expirationDate);
    if (daysRemaining < 0) {
      return {
        applicable: true,
        status: 'expired',
        applicablePositions,
        completedDate: opts.completedDate,
        expirationDate,
        daysRemaining,
        reason: `Hazardous Weather Training expired ${expirationDate}. Required for ${who}.`,
      };
    }
    if (daysRemaining <= dueSoonDays) {
      return {
        applicable: true,
        status: 'due_soon',
        applicablePositions,
        completedDate: opts.completedDate,
        expirationDate,
        daysRemaining,
        reason: `Hazardous Weather Training expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Required for ${who}.`,
      };
    }
    return {
      applicable: true,
      status: 'current',
      applicablePositions,
      completedDate: opts.completedDate,
      expirationDate,
      daysRemaining,
      reason: `Hazardous Weather Training is current. Required for ${who}.`,
    };
  }

  // No authoritative completion date on file — fall back to National's
  // own "currently owed" signal, the only thing the real import provides.
  const owesIt = applicableRoles.some((r) => r.incompleteCourses.includes(HAZARDOUS_WEATHER_COURSE_CODE));
  if (owesIt) {
    return {
      applicable: true,
      status: 'missing',
      applicablePositions,
      completedDate: null,
      expirationDate: null,
      daysRemaining: null,
      reason: `No current Hazardous Weather Training (SCO_800) completion found. Required for ${who}.`,
    };
  }
  return {
    applicable: true,
    status: 'current',
    applicablePositions,
    completedDate: null,
    expirationDate: null,
    daysRemaining: null,
    reason: `National's Trained Leaders Status report does not list SCO_800 as owed for ${who}.`,
  };
}

/** Shared by both of Unit Health's near-duplicate leader-filter
 *  implementations (the live LeadersView table and the export-fallback
 *  list used when that tab hasn't been opened yet) so the new
 *  Hazardous Weather option — and any future one — only needs to be
 *  taught here once. `m` is intentionally structural (not a direct
 *  MergedLeader import) to keep this file free of a view-layer
 *  dependency. */
export function leaderMatchesFilter(
  m: {
    memberId: string;
    anyUntrained: boolean;
    roles: { position: string; directContact: boolean; incompleteCourses: string[]; where: string }[];
  },
  filter: LeaderFilter,
  sytByMember: Map<string, { isCurrent: boolean }>,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'keyThree':
      return m.roles.some((r) => isKeyThreeRole(r.position));
    case 'untrained':
      return m.anyUntrained;
    case 'sytNotCurrent': {
      const s = sytByMember.get(m.memberId);
      return !!s && !s.isCurrent;
    }
    case 'hazardousWeather': {
      const evaluation = evaluateHazardousWeather(m.roles);
      return evaluation.status === 'missing' || evaluation.status === 'expired';
    }
    case 'councilExecBoard':
      return m.roles.some((r) => isCouncilExecutiveBoardRole(r.position));
  }
}
